import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Cell, Worksheet } from "exceljs";
import { supabase } from "../lib/supabase";
import type { Item } from "../lib/types";

type ParsedRow = {
  rowNumber: number;
  referenceId: number | null;
  brand: string;
  model: string;
  quantity: number | null;
  quantityError: string | null;
  stockArea: string;
  location: string;
  alertThreshold: number | null;
};

type ImportStatus = "new" | "update" | "unchanged" | "invalid";

type ImportPreview = ParsedRow & {
  status: ImportStatus;
  currentItem: Item | null;
  changes: string[];
  warnings: string[];
  metadataChanged: boolean;
};

type ImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  ignored: number;
  failures: string[];
  qrIds: number[];
};

type HeaderMap = {
  reference: number | null;
  brand: number;
  model: number;
  quantity: number;
  stockArea: number;
  location: number;
  alertThreshold: number | null;
};

const headerAliases = {
  reference: ["reference", "ref", "id"],
  brand: ["brand", "marque"],
  model: ["model", "modele", "designation", "modele designation"],
  quantity: ["quantity", "quantite", "stock actuel"],
  stockArea: ["stock", "bloc", "bloc stock"],
  location: ["location", "emplacement", "emplacement exact"],
  alertThreshold: ["seuil", "seuil alerte", "seuil d alerte"],
} as const;

function normalize(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function clean(value: string) {
  return value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

function normalizeStockArea(value: string) {
  const cleaned = clean(value);
  return /^asp\s*0e$/i.test(cleaned) ? "ASP 0E" : cleaned;
}

function productKey(brand: string, model: string) {
  return `${normalize(brand)}|${normalize(model)}`;
}

function cellText(cell: Cell) {
  return clean(cell.text ?? "");
}

function readInteger(cell: Cell, allowEmpty: boolean) {
  const text = cellText(cell);
  if (!text) return allowEmpty ? { value: null, error: null } : { value: null, error: "valeur manquante" };
  const numeric = typeof cell.value === "number" ? cell.value : Number(text.replace(/\s/g, "").replace(",", "."));
  if (!Number.isInteger(numeric) || numeric < 0) return { value: null, error: `« ${text} » n’est pas un nombre entier` };
  return { value: numeric, error: null };
}

function findHeader(worksheet: Worksheet) {
  const maximum = Math.min(20, worksheet.rowCount);
  for (let rowNumber = 1; rowNumber <= maximum; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const columns = new Map<string, number>();
    row.eachCell({ includeEmpty: false }, (cell, columnNumber) => {
      columns.set(normalize(cellText(cell)), columnNumber);
    });
    const find = (aliases: readonly string[]) => aliases.map((alias) => columns.get(alias)).find((value) => value !== undefined) ?? null;
    const brand = find(headerAliases.brand);
    const model = find(headerAliases.model);
    const quantity = find(headerAliases.quantity);
    const stockArea = find(headerAliases.stockArea);
    const location = find(headerAliases.location);
    if (brand && model && quantity && stockArea && location) {
      return {
        rowNumber,
        columns: {
          reference: find(headerAliases.reference),
          brand,
          model,
          quantity,
          stockArea,
          location,
          alertThreshold: find(headerAliases.alertThreshold),
        } satisfies HeaderMap,
      };
    }
  }
  throw new Error("Colonnes introuvables. Le fichier doit contenir Brand/Marque, Model/Modèle, Quantity/Quantité, Stock et Location.");
}

function parseWorksheet(worksheet: Worksheet) {
  const header = findHeader(worksheet);
  const rows: ParsedRow[] = [];
  for (let rowNumber = header.rowNumber + 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const brand = cellText(row.getCell(header.columns.brand));
    const model = cellText(row.getCell(header.columns.model));
    const stockArea = normalizeStockArea(cellText(row.getCell(header.columns.stockArea)));
    const location = cellText(row.getCell(header.columns.location)).toUpperCase();
    const referenceText = header.columns.reference ? cellText(row.getCell(header.columns.reference)) : "";
    if (normalize(referenceText) === "total") continue;
    if (!brand && !model && !stockArea && !location && !cellText(row.getCell(header.columns.quantity))) continue;
    const quantityResult = readInteger(row.getCell(header.columns.quantity), true);
    const thresholdResult = header.columns.alertThreshold
      ? readInteger(row.getCell(header.columns.alertThreshold), true)
      : { value: null, error: null };
    const referenceValue = referenceText ? Number(referenceText) : Number.NaN;
    rows.push({
      rowNumber,
      referenceId: Number.isInteger(referenceValue) && referenceValue > 0 ? referenceValue : null,
      brand,
      model,
      quantity: quantityResult.value,
      quantityError: quantityResult.error ?? thresholdResult.error,
      stockArea,
      location,
      alertThreshold: thresholdResult.value,
    });
  }
  return rows;
}

function buildPreview(rows: ParsedRow[], items: Item[]) {
  const byId = new Map(items.map((item) => [item.id, item]));
  const byProduct = new Map<string, Item[]>();
  const byBrand = new Map<string, Item[]>();
  items.forEach((item) => {
    const exact = productKey(item.brand, item.model);
    byProduct.set(exact, [...(byProduct.get(exact) ?? []), item]);
    const brand = normalize(item.brand);
    byBrand.set(brand, [...(byBrand.get(brand) ?? []), item]);
  });
  const usedIds = new Set<number>();
  const seenRows = new Set<string>();

  return rows.map((row): ImportPreview => {
    const warnings: string[] = [];
    const changes: string[] = [];
    const rowKey = productKey(row.brand, row.model);
    if (!row.brand || !row.model || !row.stockArea || !row.location) {
      return { ...row, status: "invalid", currentItem: null, changes: ["Marque, modèle, stock ou emplacement manquant"], warnings, metadataChanged: false };
    }
    if (row.quantityError) {
      return { ...row, status: "invalid", currentItem: null, changes: [row.quantityError], warnings, metadataChanged: false };
    }
    if (seenRows.has(rowKey)) {
      return { ...row, status: "invalid", currentItem: null, changes: ["Référence présente plusieurs fois dans le fichier"], warnings, metadataChanged: false };
    }
    seenRows.add(rowKey);

    let currentItem = row.referenceId ? byId.get(row.referenceId) ?? null : null;
    if (!currentItem) {
      const exactCandidates = byProduct.get(rowKey) ?? [];
      if (exactCandidates.length === 1) currentItem = exactCandidates[0];
    }
    if (!currentItem) {
      const model = normalize(row.model);
      const similarCandidates = (byBrand.get(normalize(row.brand)) ?? []).filter((item) => {
        const existingModel = normalize(item.model);
        return Math.min(model.length, existingModel.length) >= 5 && (model.includes(existingModel) || existingModel.includes(model));
      });
      if (similarCandidates.length === 1) {
        currentItem = similarCandidates[0];
        warnings.push("Correspondance probable par marque et modèle renommé");
      }
    }
    if (currentItem && usedIds.has(currentItem.id)) {
      return { ...row, status: "invalid", currentItem, changes: ["Plusieurs lignes correspondent à la même référence existante"], warnings, metadataChanged: false };
    }
    if (currentItem) usedIds.add(currentItem.id);

    if (!currentItem) {
      if (row.quantity === null) warnings.push("Quantité vide : la référence sera créée à 0 et marquée À vérifier");
      return { ...row, status: "new", currentItem: null, changes: ["Nouvelle référence et nouveau QR code"], warnings, metadataChanged: true };
    }

    if (clean(currentItem.brand) !== row.brand) changes.push(`Marque : ${currentItem.brand} → ${row.brand}`);
    if (clean(currentItem.model) !== row.model) changes.push(`Modèle : ${currentItem.model} → ${row.model}`);
    if (normalizeStockArea(currentItem.stock_area) !== row.stockArea) changes.push(`Stock : ${currentItem.stock_area} → ${row.stockArea}`);
    if (clean(currentItem.location).toUpperCase() !== row.location) changes.push(`Emplacement : ${currentItem.location} → ${row.location}`);
    if (row.alertThreshold !== null && currentItem.alert_threshold !== row.alertThreshold) changes.push(`Seuil : ${currentItem.alert_threshold} → ${row.alertThreshold}`);
    if (row.quantity === null) {
      warnings.push("Quantité vide : le stock actuel sera conservé");
    } else if (currentItem.current_quantity !== row.quantity) {
      changes.push(`Quantité : ${currentItem.current_quantity} → ${row.quantity}`);
    } else if (!currentItem.quantity_verified) {
      changes.push("Quantité désormais vérifiée");
    }
    const metadataChanged = [
      clean(currentItem.brand) !== row.brand,
      clean(currentItem.model) !== row.model,
      normalizeStockArea(currentItem.stock_area) !== row.stockArea,
      clean(currentItem.location).toUpperCase() !== row.location,
    ].some(Boolean);
    return { ...row, status: changes.length > 0 ? "update" : "unchanged", currentItem, changes, warnings, metadataChanged };
  });
}

export function InventoryImportPanel({ items, onImported }: { items: Item[]; onImported: () => Promise<void> | void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<ImportPreview[]>([]);
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);

  const summary = useMemo(() => ({
    newItems: preview.filter((row) => row.status === "new").length,
    updates: preview.filter((row) => row.status === "update").length,
    unchanged: preview.filter((row) => row.status === "unchanged").length,
    invalid: preview.filter((row) => row.status === "invalid").length,
    warnings: preview.reduce((total, row) => total + row.warnings.length, 0),
  }), [preview]);
  const actionable = summary.newItems + summary.updates;
  const visiblePreview = preview.filter((row) => row.status !== "unchanged" || row.warnings.length > 0);

  const chooseFile = async (file: File | undefined) => {
    if (!file) return;
    setReading(true); setError(""); setResult(null); setPreview([]); setFileName(file.name);
    try {
      const excelModule = await import("exceljs") as typeof import("exceljs") & { default?: typeof import("exceljs") };
      const WorkbookConstructor = excelModule.Workbook ?? excelModule.default?.Workbook;
      if (!WorkbookConstructor) throw new Error("Le lecteur Excel n’a pas pu être chargé.");
      const workbook = new WorkbookConstructor();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.worksheets[0];
      if (!worksheet) throw new Error("Le fichier ne contient aucune feuille.");
      const parsedRows = parseWorksheet(worksheet);
      if (parsedRows.length === 0) throw new Error("Aucune référence n’a été trouvée dans le fichier.");
      setPreview(buildPreview(parsedRows, items));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Le fichier Excel n’a pas pu être lu.");
    } finally {
      setReading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const runImport = async () => {
    const rows = preview.filter((row) => row.status === "new" || row.status === "update");
    if (rows.length === 0) return;
    const confirmation = window.confirm(
      `Confirmer l’import de ${rows.length} modification(s) ? ${summary.invalid > 0 ? `${summary.invalid} ligne(s) incorrecte(s) seront ignorées.` : ""} Les articles absents du fichier ne seront pas supprimés.`,
    );
    if (!confirmation) return;

    setImporting(true); setProgress(0); setError(""); setResult(null);
    const nextResult: ImportResult = { created: 0, updated: 0, unchanged: summary.unchanged, ignored: summary.invalid, failures: [], qrIds: [] };
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      try {
        const alertThreshold = row.alertThreshold ?? row.currentItem?.alert_threshold ?? 4;
        if (row.status === "new") {
          const quantity = row.quantity ?? 0;
          const { data, error: insertError } = await supabase.from("items").insert({
            brand: row.brand,
            model: row.model,
            initial_quantity: quantity,
            current_quantity: quantity,
            quantity_verified: row.quantity !== null,
            stock_area: row.stockArea,
            location: row.location,
            alert_threshold: alertThreshold,
            active: true,
          }).select("*").single();
          if (insertError || !data) throw insertError ?? new Error("Création impossible");
          nextResult.created += 1;
          nextResult.qrIds.push((data as Item).id);
        } else if (row.currentItem) {
          const metadata = {
            brand: row.brand,
            model: row.model,
            stock_area: row.stockArea,
            location: row.location,
            alert_threshold: alertThreshold,
            quantity_verified: row.quantity !== null ? true : row.currentItem.quantity_verified,
          };
          const { error: updateError } = await supabase.from("items").update(metadata).eq("id", row.currentItem.id);
          if (updateError) throw updateError;
          if (row.quantity !== null) {
            const delta = row.quantity - row.currentItem.current_quantity;
            if (delta !== 0) {
              const { error: stockError } = await supabase.rpc("admin_adjust_stock", {
                p_item_id: row.currentItem.id,
                p_quantity_delta: delta,
                p_reason: `Correction d’inventaire par import Excel (${fileName})`,
              });
              if (stockError) throw stockError;
            }
          }
          nextResult.updated += 1;
          if (row.metadataChanged) nextResult.qrIds.push(row.currentItem.id);
        }
      } catch (caught) {
        const detail = caught instanceof Error ? caught.message : "erreur inconnue";
        nextResult.failures.push(`Ligne ${row.rowNumber} · ${row.brand} ${row.model} : ${detail}`);
      }
      setProgress(index + 1);
    }
    nextResult.qrIds = [...new Set(nextResult.qrIds)];
    setResult(nextResult);
    setImporting(false);
    await onImported();
  };

  const openQrLabels = () => {
    if (!result?.qrIds.length) return;
    sessionStorage.setItem("stock-qr-import-selection", JSON.stringify(result.qrIds));
    navigate("/qr?selection=import");
  };

  return <section className="inventory-import-card no-print">
    <div className="inventory-import-intro">
      <div><p className="eyebrow">Mise à jour en masse</p><h2>Importer un inventaire Excel</h2></div>
      <p>Le site compare le fichier avec la base avant toute modification. Les utilisateurs, sorties, retours et historiques sont conservés.</p>
      <label className="secondary-button import-file-button">
        {reading ? "Lecture du fichier…" : "Choisir un fichier Excel"}
        <input ref={inputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={reading || importing} onChange={(event) => void chooseFile(event.target.files?.[0])} />
      </label>
    </div>

    {error && <div className="import-message error">{error}</div>}
    {fileName && preview.length > 0 && <div className="import-preview">
      <div className="import-preview-head"><div><strong>{fileName}</strong><span>{preview.length} ligne(s) analysée(s), y compris les lignes filtrées ou masquées</span></div></div>
      <div className="import-summary-grid">
        <div className="new"><span>Nouvelles références</span><strong>{summary.newItems}</strong></div>
        <div className="update"><span>Modifications</span><strong>{summary.updates}</strong></div>
        <div><span>Sans changement</span><strong>{summary.unchanged}</strong></div>
        <div className={summary.invalid > 0 ? "invalid" : ""}><span>Lignes incorrectes</span><strong>{summary.invalid}</strong></div>
      </div>
      {summary.warnings > 0 && <div className="import-message warning">{summary.warnings} avertissement(s) : une quantité vide conserve le stock existant. Une référence déjà marquée « À vérifier » le restera.</div>}
      {visiblePreview.length > 0 && <div className="table-scroll import-table"><table><thead><tr><th>Ligne</th><th>Article</th><th>Résultat</th><th>Détails</th></tr></thead><tbody>{visiblePreview.slice(0, 80).map((row) => <tr key={`${row.rowNumber}-${row.brand}-${row.model}`}><td>{row.rowNumber}</td><td><strong>{row.brand}</strong><span>{row.model}</span><small>{row.stockArea} · {row.location}</small></td><td><span className={`import-status ${row.status === "unchanged" ? "warning" : row.status}`}>{row.status === "new" ? "Nouveau" : row.status === "update" ? "Modifier" : row.status === "unchanged" ? "À vérifier" : "Ignorer"}</span></td><td>{[...row.changes, ...row.warnings].map((detail) => <span key={detail}>{detail}</span>)}</td></tr>)}</tbody></table>{visiblePreview.length > 80 && <p className="import-more">{visiblePreview.length - 80} autre(s) changement(s) non affiché(s).</p>}</div>}
      {!result && <div className="import-actions"><p>Aucun article absent du fichier ne sera supprimé ou désactivé.</p><button className="primary-button" type="button" disabled={importing || actionable === 0} onClick={() => void runImport()}>{importing ? `Import ${progress}/${actionable}…` : `Importer ${actionable} modification(s)`}</button></div>}
    </div>}

    {result && <div className={`import-result ${result.failures.length > 0 ? "partial" : "success"}`}>
      <div><strong>{result.failures.length > 0 ? "Import terminé avec des avertissements" : "Import terminé"}</strong><span>{result.created} nouvelle(s) référence(s), {result.updated} mise(s) à jour, {result.unchanged} sans changement, {result.ignored} ligne(s) ignorée(s).</span></div>
      {result.qrIds.length > 0 && <button className="secondary-button" type="button" onClick={openQrLabels}>Imprimer {result.qrIds.length} QR/étiquette(s)</button>}
      {result.failures.length > 0 && <ul>{result.failures.slice(0, 12).map((failure) => <li key={failure}>{failure}</li>)}</ul>}
    </div>}
  </section>;
}
